import React, { useState, useEffect } from 'react';
import { UserPlus, UserCheck, UserX, Clock } from 'lucide-react';

export default function FriendRequestButton({ targetUserId, currentUserId, targetUserName }) {
  const [friendshipStatus, setFriendshipStatus] = useState('none'); // none, pending, friends, blocked
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    if (currentUserId && targetUserId && currentUserId !== targetUserId) {
      checkFriendshipStatus();
    }
  }, [currentUserId, targetUserId]);

  const checkFriendshipStatus = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/friends/status?userId=${currentUserId}&targetUserId=${targetUserId}`
      );
      const data = await response.json();

      if (data.success) {
        setFriendshipStatus(data.status); // 'none', 'pending', 'friends', 'blocked'
      }
    } catch (error) {
      console.error('Error checking friendship status:', error);
    }
  };

  const handleSendRequest = async () => {
    if (!currentUserId || !targetUserId) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/friends/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fromUserId: currentUserId,
          toUserId: targetUserId,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setFriendshipStatus('pending');
        // Refresh the status to ensure it's accurate
        await checkFriendshipStatus();
      } else {
        alert(data.message || 'Failed to send friend request');
        // Refresh status in case something changed
        await checkFriendshipStatus();
      }
    } catch (error) {
      console.error('Error sending friend request:', error);
      alert('Failed to send friend request');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!currentUserId || !targetUserId) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/friends/request/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fromUserId: currentUserId,
          toUserId: targetUserId,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setFriendshipStatus('none');
      } else {
        alert(data.message || 'Failed to cancel friend request');
      }
    } catch (error) {
      console.error('Error canceling friend request:', error);
      alert('Failed to cancel friend request');
    } finally {
      setLoading(false);
    }
  };

  const handleUnfriend = async () => {
    if (!currentUserId || !targetUserId) return;

    const confirmUnfriend = window.confirm(`Are you sure you want to unfriend ${targetUserName}?`);
    if (!confirmUnfriend) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/friends/unfriend`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: currentUserId,
          friendId: targetUserId,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setFriendshipStatus('none');
      } else {
        alert(data.message || 'Failed to unfriend');
      }
    } catch (error) {
      console.error('Error unfriending:', error);
      alert('Failed to unfriend');
    } finally {
      setLoading(false);
    }
  };

  // Don't show button if viewing own profile
  if (!currentUserId || currentUserId === targetUserId) {
    return null;
  }

  // Friends - show unfriend button
  if (friendshipStatus === 'friends') {
    return (
      <button
        onClick={handleUnfriend}
        disabled={loading}
        className="flex items-center gap-2 px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
      >
        <UserCheck className="w-5 h-5" />
        {loading ? 'Processing...' : 'Friends'}
      </button>
    );
  }

  // Pending - show cancel request button
  if (friendshipStatus === 'pending') {
    return (
      <button
        onClick={handleCancelRequest}
        disabled={loading}
        className="flex items-center gap-2 px-6 py-2 bg-yellow-100 text-yellow-700 rounded-lg hover:bg-yellow-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
      >
        <Clock className="w-5 h-5" />
        {loading ? 'Processing...' : 'Request Pending'}
      </button>
    );
  }

  // None - show add friend button
  return (
    <button
      onClick={handleSendRequest}
      disabled={loading}
      className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
    >
      <UserPlus className="w-5 h-5" />
      {loading ? 'Sending...' : 'Add Friend'}
    </button>
  );
}
